import type { FeatureFlagMap, WorkerStateRelay, WorkerStatePayloads } from './stateRegistry';
import { effect } from '@preact/signals-react';
import { inject, injectable } from 'inversify';
import { allFlags } from 'src/lib/featureFlags/allFlags';
import type { FeatureFlagService } from 'src/services/FeatureFlagService';
import type { QueryCacheUserServiceDataSource } from 'src/services/QueryCacheUserService';

@injectable()
export class FeatureFlagWorkerStateRelay implements WorkerStateRelay<'featureFlags'> {
    readonly key = 'featureFlags';
    constructor(@inject('FeatureFlagService') readonly flags: FeatureFlagService) {}
    connect(send: (payload: FeatureFlagMap) => void): Disposable {
        const signals = Object.values(allFlags).map((flag) => [flag.key, this.flags.getSignal(flag)] as const);
        const dispose = effect(() => send(Object.fromEntries(signals.map(([key, value]) => [key, value.value]))));
        return { [Symbol.dispose]: dispose };
    }
}

@injectable()
export class QueryCacheWorkerStateRelay implements WorkerStateRelay<'user'> {
    readonly key = 'user';
    constructor(@inject('QueryCacheUserServiceDataSource') readonly source: QueryCacheUserServiceDataSource) {}
    connect(send: (payload: WorkerStatePayloads['user']) => void): Disposable {
        using resources = new DisposableStack();
        const lifetime = new AbortController();
        resources.defer(() => lifetime.abort());
        let connecting = true;
        resources.defer(
            this.source.subscribe((payload) => {
                if (connecting) {
                    send(payload);
                    return;
                }
                // User and session callbacks may run in either order during the same store/signal
                // update. Send the invalidation after those callbacks so credentials arrive first.
                queueMicrotask(() => {
                    if (!lifetime.signal.aborted) send(payload);
                });
            }),
        );
        connecting = false;
        return resources.move();
    }
}
